import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
    MoreVertical, 
    Info, 
    Shield, 
    HelpCircle,
    AlertTriangle,
    Heart,
    ExternalLink,
    X,
    LogOut,
    Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * OverflowMenu - Universal top-right menu
 * 
 * Contains non-urgent items:
 * - About
 * - Privacy
 * - Contact support
 * 
 * Soft, minimal, non-intrusive design
 */
export const OverflowMenu = ({ className }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const menuItems = [
        {
            id: 'crisis',
            label: 'Crisis Resources',
            icon: AlertTriangle,
            action: () => navigate('/crisis'),
            variant: 'crisis'
        },
        {
            id: 'divider1',
            type: 'divider'
        },
        // Show Settings only for logged-in users
        ...(user ? [{
            id: 'settings',
            label: 'Settings',
            icon: Settings,
            action: () => navigate('/settings')
        }] : []),
        {
            id: 'hypnotherapy',
            label: 'Learn about Hypnotherapy',
            icon: Heart,
            action: () => navigate('/hypnotherapy')
        },
        {
            id: 'about',
            label: 'About CoRegulate',
            icon: Info,
            action: () => navigate('/about')
        },
        {
            id: 'privacy',
            label: 'Privacy',
            icon: Shield,
            action: () => navigate('/privacy')
        },
        {
            id: 'support',
            label: 'Contact support',
            icon: HelpCircle,
            action: () => {
                window.open('mailto:support@coregulateadhd.com', '_blank');
            }
        },
        // Show Logout only for logged-in users
        ...(user ? [
            {
                id: 'divider2',
                type: 'divider'
            },
            {
                id: 'logout',
                label: 'Log out',
                icon: LogOut,
                action: () => {
                    logout();
                    navigate('/');
                },
                variant: 'danger'
            }
        ] : [])
    ];

    return (
        <div ref={menuRef} className={cn('fixed top-4 right-4 z-[60]', className)}>
            {/* Menu trigger button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'w-11 h-11 rounded-full',
                    'flex items-center justify-center',
                    'bg-[#FFBB98]/60 hover:bg-[#FFBB98]/80',
                    'backdrop-blur-md',
                    'border border-[#D8CCB8]/50 hover:border-[#D8CCB8]/70',
                    'shadow-sm hover:shadow-md',
                    'transition-all duration-300 ease-out',
                    'text-[#7D8E95] hover:text-[#1D6B6B]',
                    'hover:scale-[1.02]',
                    isOpen && 'bg-[#FFBB98]/80 border-[#D8CCB8]/70'
                )}
                data-testid="overflow-menu-btn"
                aria-label="Menu"
                aria-expanded={isOpen}
            >
                {isOpen ? (
                    <X className="w-5 h-5" />
                ) : (
                    <MoreVertical className="w-5 h-5" />
                )}
            </button>

            {/* Dropdown menu */}
            {isOpen && (
                <div 
                    className={cn(
                        'absolute top-14 right-0',
                        'w-60',
                        'bg-[#FEF6EF]/98 backdrop-blur-lg',
                        'rounded-2xl',
                        'border border-[#FFBB98]/60',
                        'shadow-xl shadow-black/5',
                        'py-2',
                        'animate-in fade-in slide-in-from-top-2 duration-200'
                    )}
                    data-testid="overflow-menu-dropdown"
                >
                    {menuItems.map((item) => {
                        if (item.type === 'divider') {
                            return (
                                <div 
                                    key={item.id} 
                                    className="h-px bg-gradient-to-r from-transparent via-[#FFBB98]/60 to-transparent my-2 mx-4"
                                />
                            );
                        }

                        const Icon = item.icon;
                        return (
                            <button
                                key={item.id}
                                onClick={() => {
                                    item.action();
                                    setIsOpen(false);
                                }}
                                className={cn(
                                    'w-full px-4 py-3',
                                    'flex items-center gap-3',
                                    'text-left text-sm font-medium',
                                    item.variant === 'danger' 
                                        ? 'text-[#B07A7A] hover:text-[#9A6A6A] hover:bg-[#FFBB98]/30'
                                        : item.variant === 'crisis'
                                        ? 'text-[#FFBB98] hover:text-[#8B5D4D] hover:bg-[#FFBB98]/20'
                                        : 'text-[#1D6B6B] hover:text-[#5A4A3A] hover:bg-[#FFBB98]/40',
                                    'transition-all duration-200'
                                )}
                                data-testid={`menu-${item.id}`}
                            >
                                <Icon className={cn(
                                    'w-4 h-4',
                                    item.variant === 'danger' ? 'text-[#B07A7A]' 
                                        : item.variant === 'crisis' ? 'text-[#FFBB98]'
                                        : 'text-[#A89070]'
                                )} />
                                <span className="flex-1">{item.label}</span>
                                {item.external && (
                                    <ExternalLink className="w-3 h-3 text-[#A89070]" />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OverflowMenu;
